import { PerformanceAudienceStoreVisits as StoreVisitsData } from '@/services/endpoints/performanceAudience';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { usePerformanceAudienceFormatters } from '../../hooks/usePerformanceAudienceFormatters';
import { paCardClass, paCardSubClass, paCardTitleClass } from '../PerformanceAudienceComponents';
import { cn } from '@/lib/utils';

type PerformanceAudienceStoreVisitsProps = {
  storeVisits: StoreVisitsData;
};

export const PerformanceAudienceStoreVisits: FC<PerformanceAudienceStoreVisitsProps> = ({ storeVisits }) => {
  const { t } = useTranslation('performanceAudience');
  const { formatNumber } = usePerformanceAudienceFormatters();

  return (
    <div className={cn(paCardClass, 'px-[26px] py-[22px]')}>
      <div className='mb-[18px] flex items-start justify-between gap-3'>
        <div className='flex items-center gap-3.5'>
          <div className='grid size-9 place-items-center rounded-[10px] bg-ar-blue-soft text-ar-blue'>
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' aria-hidden>
              <path d='M3 9l1.5-5h15L21 9' />
              <path d='M4 9v11h16V9' />
              <path d='M9 20v-6h6v6' />
            </svg>
          </div>
          <div>
            <div className={paCardTitleClass}>{t('storeVisits.title')}</div>
            <div className={paCardSubClass}>{t('storeVisits.subtitle', { count: storeVisits.locationCount })}</div>
          </div>
        </div>
        {storeVisits.status === 'active' ? (
          <span className='rounded-full bg-success-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-success-600'>
            {t('storeVisits.active')}
          </span>
        ) : null}
      </div>

      <div
        className={cn(
          'grid items-center gap-6',
          storeVisits.locations.length ? 'grid-cols-1 md:grid-cols-[minmax(12rem,1fr)_2fr]' : 'grid-cols-1'
        )}
      >
        <div>
          <div className='text-[36px] font-bold leading-none tracking-[-0.02em] text-ar-blue tabular-nums'>{formatNumber(storeVisits.total)}</div>
          <div className={paCardSubClass}>{t('storeVisits.totalLabel')}</div>
        </div>

        {storeVisits.locations.length > 0 ? (
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
            {storeVisits.locations.map((location) => (
              <div key={location.id} className='flex flex-col gap-2.5 rounded-xl border border-ink-150 bg-ink-50 p-3.5'>
                <div className='flex justify-between gap-2'>
                  <span className='text-[13px] font-semibold text-ink-900'>{location.name}</span>
                  <span className='text-base font-semibold text-ar-blue'>{formatNumber(location.visits)}</span>
                </div>
                <div className='h-1.5 overflow-hidden rounded-full bg-ink-200'>
                  <div
                    className='h-full rounded-full bg-ar-blue'
                    style={{ width: `${location.share}%` }}
                    role='progressbar'
                    aria-valuenow={location.share}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={location.name}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};
